/**
 * The step-by-step guide shown beside every screen of the app window.
 * Each flow is a short list of steps; a screen says how far along it is
 * (`progress` = index of the current step; earlier steps are done).
 */

import { t, type MessageKey } from '@/i18n';

export type FlowId = 'overview' | 'secret' | 'chat' | 'receive' | 'guestSecret' | 'guestChat' | 'guestGate' | 'connect';
export type StepState = 'done' | 'current' | 'todo';

interface FlowStep {
  readonly title: string;
  readonly text: string;
}

export interface Flow {
  readonly title: string;
  readonly steps: readonly FlowStep[];
}

/** A flow whose words are read when they are shown, so they follow the language. */
function flow(title: MessageKey, steps: readonly (readonly [MessageKey, MessageKey])[]): Flow {
  return {
    get title() {
      return t(title);
    },
    steps: steps.map(([name, text]) => ({
      get title() {
        return t(name);
      },
      get text() {
        return t(text);
      },
    })),
  };
}

export const FLOWS: Record<FlowId, Flow> = {
  overview: flow('flows.overview.title', [
      ['flows.overview.s1Title', 'flows.overview.s1Text'],
      ['flows.overview.s2Title', 'flows.overview.s2Text'],
      ['flows.overview.s3Title', 'flows.overview.s3Text'],
      ['flows.overview.s4Title', 'flows.overview.s4Text'],
  ]),
  secret: flow('flows.secret.title', [
      ['flows.secret.s1Title', 'flows.secret.s1Text'],
      ['flows.secret.s2Title', 'flows.secret.s2Text'],
      ['flows.secret.s3Title', 'flows.secret.s3Text'],
      ['flows.secret.s4Title', 'flows.secret.s4Text'],
      ['flows.secret.s5Title', 'flows.secret.s5Text'],
  ]),
  chat: flow('flows.chat.title', [
      ['flows.chat.s1Title', 'flows.chat.s1Text'],
      ['flows.chat.s2Title', 'flows.chat.s2Text'],
      ['flows.chat.s3Title', 'flows.chat.s3Text'],
      ['flows.chat.s4Title', 'flows.chat.s4Text'],
      ['flows.chat.s5Title', 'flows.chat.s5Text'],
  ]),
  receive: flow('flows.receive.title', [
      ['flows.receive.s1Title', 'flows.receive.s1Text'],
      ['flows.receive.s2Title', 'flows.receive.s2Text'],
      ['flows.receive.s3Title', 'flows.receive.s3Text'],
      ['flows.receive.s4Title', 'flows.receive.s4Text'],
      ['flows.receive.s5Title', 'flows.receive.s5Text'],
  ]),
  guestSecret: flow('flows.guestSecret.title', [
      ['flows.guestSecret.s1Title', 'flows.guestSecret.s1Text'],
      ['flows.guestSecret.s2Title', 'flows.guestSecret.s2Text'],
      ['flows.guestSecret.s3Title', 'flows.guestSecret.s3Text'],
      ['flows.guestSecret.s4Title', 'flows.guestSecret.s4Text'],
  ]),
  guestChat: flow('flows.guestChat.title', [
      ['flows.guestChat.s1Title', 'flows.guestChat.s1Text'],
      ['flows.guestChat.s2Title', 'flows.guestChat.s2Text'],
      ['flows.guestChat.s3Title', 'flows.guestChat.s3Text'],
      ['flows.guestChat.s4Title', 'flows.guestChat.s4Text'],
  ]),
  guestGate: flow('flows.guestGate.title', [
      ['flows.guestGate.s1Title', 'flows.guestGate.s1Text'],
      ['flows.guestGate.s2Title', 'flows.guestGate.s2Text'],
      ['flows.guestGate.s3Title', 'flows.guestGate.s3Text'],
  ]),
  connect: flow('flows.connect.title', [
      ['flows.connect.s1Title', 'flows.connect.s1Text'],
      ['flows.connect.s2Title', 'flows.connect.s2Text'],
      ['flows.connect.s3Title', 'flows.connect.s3Text'],
      ['flows.connect.s4Title', 'flows.connect.s4Text'],
  ]),
};

/** State of step `index` when the flow has reached `progress` (null = an overview with no progress). */
export function stepState(index: number, progress: number | null): StepState {
  if (progress === null) return 'todo';
  if (index < progress) return 'done';
  return index === progress ? 'current' : 'todo';
}

/** Where the owner of a bridge is, from what the server knows. A chat is done once the other person has written. */
export function ownerProgress(flow: 'secret' | 'chat' | 'receive', facts: { readonly views: number; readonly guestMessages: number }): number {
  if (flow === 'secret') return facts.views > 0 ? 4 : 2;
  if (flow === 'chat') return facts.guestMessages > 0 ? 5 : facts.views > 0 ? 4 : 2;
  if (facts.guestMessages > 0) return 5;
  return facts.views > 0 ? 3 : 2;
}
